import { GAP_LABEL } from '../../constants/exclusions.js';

// One card per mapping gap, answering the three questions a gap chip never
// could: what we already know (and which system it came from), the ONE thing
// we went looking for and didn't find, and where someone goes to fix it.
// Backend builds the wording in core/gap_explain.py so the panel can never
// claim a different search than the engine actually ran.

const SIDE = {
  dayforce: { label: 'Fix in Dayforce', bg: '#fff0cc', fg: '#6e4c00', bd: '#e6ba59' },
  netsuite: { label: 'Fix in NetSuite', bg: '#e2e3f3', fg: '#3c3f7c', bd: '#c3c5e8' },
};

function Chip({ c }) {
  const known = c.value != null && c.value !== '';
  return (
    <span style={{
      display: 'inline-block', margin: '0 6px 4px 0', padding: '2px 8px',
      borderRadius: 4, fontSize: 12,
      background: known ? '#fff' : '#f1f1f1',
      border: `1px solid ${known ? '#dcdcdc' : '#e4e4e4'}`,
      color: known ? '#333' : '#999',
    }}
    >
      <span style={{ color: '#888' }}>{c.label}:</span>{' '}
      {known ? <strong style={{ fontWeight: 600 }}>{c.value}</strong> : <em>not available</em>}
      {known && c.hint ? <span style={{ color: '#999' }}> · {c.hint}</span> : null}
    </span>
  );
}

function GapCard({ g }) {
  const side = SIDE[g.side] || SIDE.netsuite;
  // A gap the engine never got to attempt (no location ⇒ no 419 lookup, no
  // billing title ⇒ no 420 lookup). Dimmed, because chasing it is wasted
  // work until the gap it follows from is fixed.
  const downstream = Boolean(g.blocked_by);
  return (
    <div style={{
      marginTop: 8, padding: '10px 12px', borderRadius: 4,
      background: downstream ? '#fafafa' : '#fff',
      border: `1px solid ${downstream ? '#e6e6e6' : '#f0c4c8'}`,
      borderLeft: `4px solid ${downstream ? '#d0d0d0' : side.bd}`,
      opacity: downstream ? 0.85 : 1,
    }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong style={{ color: downstream ? '#666' : '#721c24', fontSize: 13 }}>
          {GAP_LABEL[g.key] || g.key}
        </strong>
        <span style={{
          padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
          background: side.bg, color: side.fg,
        }}
        >
          {side.label}
        </span>
        {downstream ? (
          <span
            style={{
              padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
              background: '#eee', color: '#666',
            }}
            title={'The engine never ran this lookup — an earlier mapping failed first. '
                   + 'Fix the gap it follows from and this one clears on the next reprocess.'}
          >
            follows from: {GAP_LABEL[g.blocked_by] || g.blocked_by}
          </span>
        ) : null}
      </div>

      <div style={{ marginTop: 6, fontSize: 13, color: '#333' }}>
        <span style={{ color: '#888' }}>Looking for </span>
        {g.missing}
        {g.where ? <span style={{ color: '#888' }}> — in {g.where}</span> : null}
      </div>

      {g.have && g.have.length > 0 ? (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>
            Matched on the values we do have:
          </div>
          {g.have.map((c, i) => <Chip key={`${c.label}-${i}`} c={c} />)}
        </div>
      ) : null}

      {g.fix ? (
        <div style={{ marginTop: 4, fontSize: 12, color: '#555' }}>
          <span style={{ color: '#888' }}>Fix: </span>{g.fix}
        </div>
      ) : null}
    </div>
  );
}

export function GapDetails({ gaps, details }) {
  const keys = gaps || [];
  if (keys.length === 0) return null;

  // Detail payloads cached before this shipped (or a record whose gap key the
  // backend doesn't explain yet) still render the plain label list.
  if (!details || details.length === 0) {
    return (
      <div style={{ marginTop: 12, color: '#721c24', fontSize: 13 }}>
        <strong>Mapping Gaps:</strong> {keys.map((g) => GAP_LABEL[g] || g).join(', ')}
      </div>
    );
  }

  const real = details.filter((g) => !g.blocked_by).length;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ color: '#721c24', fontSize: 13 }}>
        <strong>Mapping Gaps ({details.length})</strong>
        {real !== details.length ? (
          <span style={{ color: '#888', marginLeft: 8, fontSize: 11 }}>
            {real} to fix · {details.length - real} follow on from {real === 1 ? 'it' : 'them'}
          </span>
        ) : null}
      </div>
      {details.map((g, i) => <GapCard key={`${g.key}-${i}`} g={g} />)}
    </div>
  );
}
